import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { addAiPlayer, fetchAiPlayers } from '../../../lib/api/ai'
import { createRoom, joinRoomByCode, startGame } from '../../../lib/api/rooms'
import { useAuth } from '../../../lib/auth'
import { useGatedAction } from '../../../lib/useSubscription'
import { readError } from '../../../lib/errors'
import type { AiPlayerRow } from '../../../lib/database.types'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onEnteredRoom: (roomId: string) => void
  onOnlineMatch: () => void
  onPassPlay: () => void
  onOpenWeekly: () => void
  onOpenPaywall: () => void
}

const MIN_AI = 1
// 6-seat table; subtract the host = up to 5 AI in Quick Play.
const MAX_AI = 5
// Sensible starting selection — first DEFAULT_AI personas the DB
// returns (alphabetical), user can toggle from there.
const DEFAULT_AI = 3

/**
 * IET lobby — mirrors the Colorful lobby. The main flow is "Quick
 * Play vs AI": pick the AI count, swap personas if you don't like
 * the suggested lineup, then tap to spin up a private room with the
 * chosen AIs and drop into the live game. Party play and online
 * matchmaking sit below as alternative modes.
 */
export function LobbyScreen({
  onBack, onEnteredRoom, onOnlineMatch, onPassPlay, onOpenWeekly, onOpenPaywall,
}: Props) {
  const t = useT()
  const { profile, configured, updateProfile } = useAuth()
  const gate = useGatedAction(onOpenPaywall)
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose')
  const [isPrivate, setIsPrivate] = useState(false)
  const [roomCode, setRoomCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Display-name draft (same shape as Colorful lobby). Skip the literal
  // "Player" fallback so the input visibly needs filling out instead of
  // letting the user submit with the placeholder name.
  const initialName = profile?.display_name && profile.display_name !== 'Player'
    ? profile.display_name : ''
  const [nameDraft, setNameDraft] = useState<string>(initialName)
  useEffect(() => {
    if (profile?.display_name && profile.display_name !== 'Player') {
      setNameDraft(profile.display_name)
    }
  }, [profile?.display_name])

  // AI roster state — pool is the full active roster from the DB; the
  // user toggles which personas to include via `selectedIds`. Same
  // multi-select pattern Three Step uses, replacing the old counter +
  // per-slot Swap button. Showing all 10 at once with a tap-to-toggle
  // checkmark reads faster than cycling through with Swap.
  const [pool, setPool] = useState<AiPlayerRow[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    fetchAiPlayers()
      .then(rows => {
        if (cancelled) return
        setPool(rows)
        setSelectedIds(prev => {
          if (prev.size > 0) return prev   // user already customized
          return new Set(rows.slice(0, DEFAULT_AI).map(r => r.id))
        })
      })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  // Derived lineup: personas the user has actually selected, in pool order.
  const lineup = (pool ?? []).filter(p => selectedIds.has(p.id))

  function togglePersona(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (next.size <= MIN_AI) return prev   // need ≥1 to play
        next.delete(id)
      } else {
        if (next.size >= MAX_AI) return prev   // capped
        next.add(id)
      }
      return next
    })
  }

  async function persistNameIfChanged(): Promise<void> {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === profile?.display_name) return
    try { await updateProfile({ display_name: trimmed }) }
    catch (e) { console.warn('[iet-lobby] name update failed, continuing anyway', e) }
  }

  function canPlay(): boolean {
    if (!configured) { setError(t('Supabase is not configured yet. See SUPABASE_SETUP.md.')); return false }
    if (!profile)    { setError(t('Sign in to play.')); return false }
    return true
  }

  /**
   * Quick Play: spin up a private room with the chosen AI roster and
   * skip the table-lobby step entirely — fire start_game so the
   * LiveRoomScreen lands directly in the dealing/building phase.
   * Party Play with friends still goes through the lobby because
   * the host has to share the room code first.
   */
  async function startQuickPlay() {
    if (!canPlay()) return
    if (lineup.length < MIN_AI) {
      setError(t('Pick at least one AI opponent.'))
      return
    }
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createRoom(true)
      // Add personas in lineup order. Sequential keeps seat order
      // deterministic — and the count is small (≤4).
      for (const persona of lineup) {
        await addAiPlayer(room.id, persona.id)
      }
      // Auto-start so the user lands in the live game, not the
      // table lobby (which is reserved for party-play hosts who
      // need to share the code).
      await startGame(room.id)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  async function doCreate() {
    if (!canPlay()) return
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createRoom(isPrivate)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  async function doJoin() {
    if (!canPlay() || roomCode.length !== 6) return
    if (!nameDraft.trim()) {
      setError(t('Type a name first. It shows up next to your seat.'))
      return
    }
    setBusy(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await joinRoomByCode(roomCode)
      onEnteredRoom(room.id)
    } catch (err) {
      setError(readError(err))
    } finally {
      setBusy(false)
    }
  }

  // ─── Choose mode ─────────────────────────────────────────────────────────
  if (mode === 'choose') {
    const ready = pool !== null && lineup.length >= MIN_AI && !busy
    return (
      <div className="screen">
        <BackgroundTitle text="I'd Eat That" color="#B58615" />
        <PageHeader title="I'd Eat That" subtitle={t('3-course storytelling')} onBack={onBack} />

        <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
          {/* Your name */}
          <div>
            <label htmlFor="iet-lobby-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="iet-lobby-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={() => { void persistNameIfChanged() }}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                         focus:ring-brand-lime/30 transition-all"
            />
          </div>

          {/* How it plays */}
          <div className="iet-gold rounded-2xl shadow-card p-4
                          border-2 border-game-iet-dark">
            <p className="text-xs font-semibold text-brand-text uppercase tracking-wide mb-2">
              {t('How it plays')}
            </p>
            <ul className="text-sm text-brand-text space-y-1.5">
              <li>{t('• Each round, pick three ingredients from your hand and write a dish title and short story for each course.')}</li>
              <li>{t("• Read everyone's dish aloud. Vote on whose you'd actually eat. Most votes wins.")}</li>
              <li>{t('• Method cards are wild and pair with any ingredient.')}</li>
            </ul>
          </div>

          {/* Play with friends — promoted above the AI picker so the
              social option leads. The "Or" prefix moves to the AI
              section below per the cross-game consistency pass. */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('Play with friends')}
            </p>

            <button
              onClick={() => gate(() => setMode('create'))}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🎉</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Party play with friends')}</p>
                <p className="text-xs text-brand-muted">
                  {t('Host a table, public or private. Share a code.')}
                </p>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🔑</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Join a room')}</p>
                <p className="text-xs text-brand-muted">{t('Enter a 6-character code from a friend.')}</p>
              </div>
            </button>

            {/* Online matchmade — same wiring as Colorful. */}
            <button
              onClick={() => { if (!busy) gate(onOnlineMatch) }}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">🌐</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Online match')}</p>
                <p className="text-xs text-brand-muted">{t('Get matched with 3 other players. Take turns on your own time. The game waits for you.')}</p>
              </div>
            </button>

            <button
              onClick={() => { if (!busy) gate(onPassPlay) }}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">📱</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Phone Pass Play')}</p>
                <p className="text-xs text-brand-muted">{t('Local only. Pass the phone between people in the room. No internet needed.')}</p>
              </div>
            </button>

            <button
              onClick={() => { if (!busy) onOpenWeekly() }}
              disabled={busy}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors disabled:opacity-60"
            >
              <span className="text-2xl shrink-0">📆</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-brand-text">{t('Play the weekly')}</p>
                <p className="text-xs text-brand-muted">
                  {t('One ingredient list for the whole community.')}
                </p>
              </div>
            </button>
          </div>

          {/* Or play with AI opponents — multi-select picker, sits
              below the friends section now. */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
                {t('Or play with AI opponents')}
              </p>
              <p className="text-xs text-brand-muted">
                {lineup.length}/{MAX_AI} picked
              </p>
            </div>
            {error && (
              <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-2">
                <p className="text-sm text-brand-danger">{error}</p>
              </div>
            )}
            {pool === null ? (
              <div className="space-y-2">
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
                <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
              </div>
            ) : (
              <ul className="space-y-2">
                {pool.map(p => {
                  const selected = selectedIds.has(p.id)
                  const atCap = !selected && lineup.length >= MAX_AI
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => togglePersona(p.id)}
                        disabled={atCap}
                        aria-pressed={selected}
                        className={
                          'w-full p-3 flex items-center gap-3 rounded-2xl text-left ' +
                          'transition-colors active:scale-[0.99] ' +
                          (selected
                            ? 'iet-gold border-2 border-game-iet-dark shadow-card'
                            : 'bg-white border border-brand-border ') +
                          (atCap ? ' opacity-40' : '')
                        }
                      >
                        <span className="text-2xl shrink-0">{p.avatar_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-brand-text text-sm">{p.name}</p>
                          <p className="text-xs text-brand-muted line-clamp-2">{t(p.persona)}</p>
                        </div>
                        <span
                          className={
                            'shrink-0 w-6 h-6 rounded-full flex items-center justify-center ' +
                            'text-sm font-bold border-2 transition-colors ' +
                            (selected
                              ? 'bg-game-iet-dark border-game-iet-dark text-white'
                              : 'bg-white border-brand-border text-brand-muted')
                          }
                          aria-hidden="true"
                        >
                          {selected ? '✓' : '+'}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="floating-actions">
          <button
            onClick={() => gate(startQuickPlay)}
            disabled={!ready}
            className="btn-iet disabled:opacity-40"
          >
            {busy ? t('Setting up table…') : t('Quick Play vs AI')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Join mode ───────────────────────────────────────────────────────────
  if (mode === 'join') {
    return (
      <div className="screen">
        <PageHeader title={t('Join Room')} onBack={() => { setMode('choose'); setError(null) }} />
        <div className="flex-1 px-5 py-8 space-y-6">
          <div className="text-center">
            <img src="/games/ideatthat.webp" alt="I'd Eat That"
                 className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
            <h2 className="font-display text-xl font-bold text-brand-text">{t('Enter room code')}</h2>
            <p className="text-brand-muted text-sm mt-1">{t('Ask your host for the 6-character code')}</p>
          </div>
          <div>
            <label htmlFor="iet-join-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="iet-join-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={() => { void persistNameIfChanged() }}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                         text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-iet-dark"
            />
          </div>
          <div>
            <label htmlFor="iet-join-code"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Room code')}
            </label>
            <input
              id="iet-join-code"
              type="text"
              value={roomCode}
              onChange={e => setRoomCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
              placeholder="FEAST7"
              inputMode="text"
              autoCapitalize="characters"
              className="w-full px-4 py-4 rounded-2xl border border-brand-border bg-white
                         text-brand-text text-center text-2xl font-display font-bold
                         tracking-[0.2em] placeholder:text-brand-border
                         focus:outline-hidden focus:border-game-iet-dark"
            />
          </div>
          {error && <p className="text-sm text-brand-danger text-center">{error}</p>}
          <button
            onClick={doJoin}
            disabled={busy || roomCode.length !== 6 || !nameDraft.trim()}
            className="btn-iet"
          >
            {busy ? 'Joining…' : t('Join Room')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Create mode ─────────────────────────────────────────────────────────
  return (
    <div className="screen">
      <PageHeader title={t('Create Room')} onBack={() => { setMode('choose'); setError(null) }} />
      <div className="flex-1 px-5 py-8 space-y-6">
        <div className="text-center">
          <img src="/games/ideatthat.webp" alt="I'd Eat That"
               className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
          <h2 className="font-display text-xl font-bold text-brand-text">{t('New table')}</h2>
          <p className="text-brand-muted text-sm mt-1">{t("You'll get a 6-character code to share.")}</p>
        </div>

        <label className="card p-4 flex items-center justify-between cursor-pointer">
          <div>
            <p className="font-semibold text-brand-text">{t('Private room')}</p>
            <p className="text-sm text-brand-muted mt-0.5">
              {t('Only people with the code can join. Hidden from Quick Play.')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isPrivate}
            onClick={() => setIsPrivate(v => !v)}
            className={`relative w-12 h-7 rounded-full transition-colors ${isPrivate ? 'bg-game-iet-dark' : 'bg-brand-border'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-xs
                              transition-transform ${isPrivate ? 'translate-x-5' : ''}`} />
          </button>
        </label>

        {error && <p className="text-sm text-brand-danger text-center">{error}</p>}

        <button onClick={doCreate} disabled={busy} className="btn-iet">
          {busy ? 'Creating…' : t('Create Room')}
        </button>
      </div>
    </div>
  )
}
